/**
 * Per-tab memory of which dashboard board (workspace) this tab is showing.
 *
 * The dashboard layout — card positions, filters, AND the selected board — is one
 * per-user record on the server, rewritten in full on almost any change (a resize,
 * a filter tweak, a board switch). With two app tabs open, the last tab to save
 * decided which board *every* later mount opened on, so a second tab silently
 * dragged the first one onto its board.
 *
 * sessionStorage is per browser tab and is not shared with other tabs, so a tab
 * that has chosen a board keeps it. The server value still wins for a brand-new
 * tab, which keeps "open my last-used board" working.
 *
 * Every access is wrapped: sessionStorage throws in some privacy configurations,
 * and losing the preference must never take the dashboard down with it.
 */

const storageKey = (dashboard: string) => `dashboard_workspace_${dashboard}`;

/** The board this tab last selected, or null if it hasn't chosen one yet. */
export function readTabWorkspaceId(dashboard: string): string | null {
  try {
    return window.sessionStorage.getItem(storageKey(dashboard));
  } catch {
    return null;
  }
}

/** Remember the board this tab is on. Never throws. */
export function writeTabWorkspaceId(dashboard: string, workspaceId: string): void {
  try {
    window.sessionStorage.setItem(storageKey(dashboard), workspaceId);
  } catch {
    // No per-tab memory available — fall back to the server-saved board.
  }
}

/**
 * Which board to open on: this tab's own selection first, then the server-saved
 * one, then 'default'. A board the user no longer has is skipped at each step.
 */
export function resolveWorkspaceId({
  tabId,
  savedId,
  available,
  fallback = 'default',
}: {
  tabId: string | null | undefined;
  savedId: string | null | undefined;
  available: string[];
  fallback?: string;
}): string {
  if (tabId && available.includes(tabId)) return tabId;
  if (savedId && available.includes(savedId)) return savedId;
  return fallback;
}
